import * as React from 'react';
import { classNames } from 'utils/ui/css';

type TDialogAttributes = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'role' | 'aria-modal' | 'aria-labelledby' | 'aria-describedby'
>;

interface IDialogShellProps {
  children: React.ReactNode;
  rootProps?: TDialogAttributes;
  panelProps?: TDialogAttributes;
  rootTestId?: string;
  panelTestId?: string;
  /** Panel width and padding: `default` is `sm:max-w-lg sm:p-6`, `compact` is a 480px panel with 22px padding. */
  size?: TDialogSize;
  /**
   * Where the panel sits in the viewport: `center` is bottom on mobile and centred on desktop, `top` keeps
   * the panel's top edge fixed so content that grows (an open list) does not move it.
   */
  anchor?: TDialogAnchor;
}

type TDialogSize = 'default' | 'compact';
type TDialogAnchor = 'center' | 'top';

// The base, size and anchor strings must not share a Tailwind prefix: `classNames` keeps only the first
// class of a conflict group, so a clash would silently drop the variant's class.
const PANEL_BASE_CLASSES =
  'relative overflow-hidden px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full';
const PANEL_SIZE_CLASSES: Record<TDialogSize, string> = {
  default: 'sm:max-w-lg sm:p-6',
  compact: 'sm:max-w-[480px] sm:p-[22px]',
};

const FRAME_BASE_CLASSES = 'flex min-h-full justify-center text-center';
const FRAME_ANCHOR_CLASSES: Record<TDialogAnchor, string> = {
  center: 'items-end p-4 sm:items-center sm:p-0',
  top: 'items-start px-4 pb-4 pt-[10vh] sm:items-start sm:px-0 sm:pb-0 sm:pt-[15vh]',
};

const PANEL_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--ex3d-dialog-bg-color, var(--ex3d-dealer-white))',
  color: 'var(--ex3d-dialog-text-color, var(--ex3d-dealer-gray-900))',
  borderRadius: 'var(--ex3d-dialog-border-radius, 0.5rem)',
  fontFamily: 'var(--ex3d-dialog-font-family, inherit)',
};

/** Shared backdrop, viewport frame, and themed panel for modal dialogs. */
export default function DialogShell({
  children,
  rootProps,
  panelProps,
  rootTestId,
  panelTestId,
  size = 'default',
  anchor = 'center',
}: IDialogShellProps) {
  return (
    <div {...rootProps} data-testid={rootTestId} className="relative z-[101]">
      <div
        part="dialog-backdrop"
        className="fixed inset-0 transition-opacity"
        style={{
          backgroundColor: 'var(--ex3d-dialog-backdrop-color, var(--groupui-vwgroup-ref-color-deep-space-blue-900-80))',
        }}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[102] w-screen overflow-y-auto">
        <div className={classNames(FRAME_BASE_CLASSES, FRAME_ANCHOR_CLASSES[anchor])}>
          <div
            {...panelProps}
            data-testid={panelTestId}
            part="dialog"
            className={classNames(PANEL_BASE_CLASSES, PANEL_SIZE_CLASSES[size])}
            style={PANEL_STYLE}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
